import {
  AnyScopedIdsConfig,
  AtomicOperation,
  ScopedIdsConfig,
} from "../../atomic-operations/index.js";
import { AuthRule } from "../../AuthRule.js";
import { CellId } from "../../idTypeBrands.js";
import { PermissionedEntityTypeName } from "../../PermissionedEntityTypeName.js";
import { SemanticCap } from "../../semanticCaps.js";
import type { internalHexVersionIdPayloadKey } from "../HexVersionAtomicOperation.js";
import {
  HexVersionAtomicOperationDefinitionExtras,
  createHexVersionAtomicOperationDefinition,
} from "../HexVersionAtomicOperationDefinition.js";

import { CREATE_CELL } from "./createCellOperation.js";

type UpdateCellContentsAuthArg<
  FIELD_NAME extends string,
  ADDITIONAL_FIELD_ARGS extends object,
> =
  | FIELD_NAME
  | "cellId"
  | "key"
  | "value"
  | (keyof ADDITIONAL_FIELD_ARGS & string)
  | typeof internalHexVersionIdPayloadKey;

/** The payload shape every operation built by this factory carries. */
type UpdateCellContentsPayload<
  FIELD_NAME extends string,
  ID_TYPE extends string,
  UPDATABLE_FIELDS extends object,
  ADDITIONAL_FIELD_ARGS extends object,
  // Matches the generic parameter on `create` below, so the type this factory
  // casts `scopedIds` to is the same one the definition is inferred against.
  K extends string & keyof UPDATABLE_FIELDS = string & keyof UPDATABLE_FIELDS,
> = { [K2 in FIELD_NAME]: ID_TYPE } & {
  cellId: CellId;
  key: K;
  value: UPDATABLE_FIELDS[K];
} & ADDITIONAL_FIELD_ARGS;

type MakeUpdateCellContentsOperationArgs<
  ACTION_TYPE extends `UPDATE_${string}_CELL`,
  ENTITY_NAME extends `${string}Cell`,
  FIELD_NAME extends `${Uncapitalize<ENTITY_NAME>}Id`,
  AUTH_ARG extends string,
> = {
  type: ACTION_TYPE;
  entityName: ENTITY_NAME;
  fieldName: FIELD_NAME;
  customWriteAuth?: {
    authRule: AuthRule<AUTH_ARG>;
    reason: string;
  };
};

/**
 * This function needs to be called twice - the first time with an explicit generic.
 * For example:
 *
 * ```
 * const UPDATE_CODE_CELL =
 *   makeUpdateCellContentsOperation<CodeCellUpdatableFields, CodeCellId>()({
 *     type: "UPDATE_CODE_CELL",
 *     entityName: "CodeCell",
 *     fieldName: "codeCellId",
 *   });
 * ```
 * This "double call" makes it so you can provide one explicit generic
 * without having to provide so many others.
 *
 * @returns An AtomicOperationDefinition for updating cell fields for a specific cell type
 */
// Inferring the return type gives us better inference for `create` args
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const makeUpdateCellContentsOperation =
  <
    UPDATABLE_FIELDS extends object,
    ID_TYPE extends string,
    ADDITIONAL_FIELD_ARGS extends object = {}, // eslint-disable-line @typescript-eslint/no-empty-object-type
  >() =>
  <
    ACTION_TYPE extends `UPDATE_${string}_CELL`,
    ENTITY_NAME extends PermissionedEntityTypeName & `${string}Cell`,
    FIELD_NAME extends `${Uncapitalize<ENTITY_NAME>}Id`,
  >({
    entityName,
    fieldName,
    type,
    customWriteAuth,
    scopedIds: additionalScopedIds,
    ...extras
  }: MakeUpdateCellContentsOperationArgs<
    ACTION_TYPE,
    ENTITY_NAME,
    FIELD_NAME,
    UpdateCellContentsAuthArg<FIELD_NAME, ADDITIONAL_FIELD_ARGS>
  > &
    // `scopedIds` is replaced with an optional, erased form: this factory
    // declares `cellId` and `fieldName` itself, so a caller only supplies
    // entries for ids it introduces through `ADDITIONAL_FIELD_ARGS`.
    Omit<
      HexVersionAtomicOperationDefinitionExtras<
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (...args: any[]) => AtomicOperation<
          string,
          {
            [K2 in typeof fieldName]: ID_TYPE;
          } & {
            cellId: CellId;
            key: keyof UPDATABLE_FIELDS;
            value: UPDATABLE_FIELDS[keyof UPDATABLE_FIELDS];
          } & ADDITIONAL_FIELD_ARGS
        >
      >,
      "scopedIds"
    > & { scopedIds?: AnyScopedIdsConfig }) => {
    // The two ids this factory's own `writeAuth` authorizes against, declared
    // here so all fourteen callers do not repeat them. `additionalScopedIds`
    // carries ids a caller introduces through `ADDITIONAL_FIELD_ARGS`.
    const scopedIds: AnyScopedIdsConfig = {
      cellId: { scope: "ownedByWrapper", idType: "Cell" },
      [fieldName]: { scope: "ownedByWrapper", idType: entityName },
      ...additionalScopedIds,
    };

    return createHexVersionAtomicOperationDefinition({
      ...extras,
      type,
      readAuth: {
        kind: "hasSemanticCap",
        cap: SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
      },
      writeAuth: customWriteAuth?.authRule ?? {
        kind: "and",
        and: [
          {
            kind: "hasSemanticCapOnIdArg",
            cap: SemanticCap.EDIT_PROJECT_CONTENTS,
            idArg: "cellId",
            idType: "Cell",
          },
          {
            kind: "hasSemanticCapOnIdArg",
            idArg: fieldName,
            idType: entityName,
            cap: SemanticCap.EDIT_PROJECT_CONTENTS,
          },
        ],
      },
      // `ENTITY_NAME` and `FIELD_NAME` are unresolved type parameters here, so
      // TypeScript cannot check a computed key against a mapped type over them,
      // even though their constraints already guarantee `fieldName` is
      // `${Uncapitalize<ENTITY_NAME>}Id` — the exact relationship
      // `EntityTypeNameOfId` computes. Every caller is checked against its own
      // concrete payload.
      scopedIds: scopedIds as ScopedIdsConfig<
        UpdateCellContentsPayload<
          FIELD_NAME,
          ID_TYPE,
          UPDATABLE_FIELDS,
          ADDITIONAL_FIELD_ARGS
        >
      >,
      logSafe: ["cellId", fieldName, "key"],
      conflictId: (op) => `${type}-${op.payload[fieldName]}-${op.payload.key}`,
      creationId: (op) => `${CREATE_CELL.type}-${op.payload.cellId}`,
      create: <K extends string & keyof UPDATABLE_FIELDS>(
        args: {
          [K2 in typeof fieldName]: ID_TYPE;
        } & {
          cellId: CellId;
          key: K;
          value: UPDATABLE_FIELDS[K];
        } & ADDITIONAL_FIELD_ARGS,
      ) => ({
        type,
        payload: {
          ...args,
        },
      }),
    });
  };
